import type { FieldObject } from '../../lib/FieldObject';
import type { EditableEventData, ActionButtonClickedData } from '../../lib/messages/SnippetMessage';
import type { StudioFrameActions } from '../../actions/studio/frame';
import { OutlineButtonType } from '../../snippet/components/Outline/types';
import { RESET_STUDIO_ACTION } from '../consts';

export interface State {
    urlPath: string;
    urlPathAttrs: {
        isReplace: boolean;
        isUserTriggered: boolean;
    };
    urlPathHistory: string[];
    urlPathHistoryIndex: number;
    urlPathProgress: string | null;
    isAvailable: boolean;
    element: HTMLIFrameElement | null;
    isFrameLoaded: boolean;
    isSnippetLoaded: boolean;
    snippetUrl?: string;
    isSnippetReady: boolean;
    isSnippetReadyMessageReceived: boolean;
    isAppLoaded: boolean;
    isUnloading: boolean;
    isNavigateClicked: boolean;
    scrollPosition: { x: number; y: number };
    loadProgress: number;
    maxLoadProgress: number | null;
    loadStartDate: Date | null;
    lastEditableEvent: EditableEventData | null;
    lastActionButton: ActionButtonClickedData | null;
    lastHighlightedField: FieldObject | null;
    editableField: FieldObject | null;
    authToken: string | null;
}

const getDefaultState = (): State => ({
    /**
     * The URL path of the preview frame. Normalized to have a leading slash
     * and no trailing slash.
     */
    urlPath: '',

    urlPathAttrs: {
        /**
         * Navigate or replace
         */
        isReplace: false,

        /**
         * User triggered path change via clicking on some link
         */
        isUserTriggered: false
    },

    /**
     * A record of paths the user has navigated to.
     */
    urlPathHistory: [],

    /**
     * This path's index in the history.
     */
    urlPathHistoryIndex: -1,

    /**
     * Path which page is being changed to
     */
    urlPathProgress: null,

    /**
     * Frame is available within the Studio
     */
    isAvailable: false,

    /**
     * iframe dom element
     */
    element: null,

    /**
     * Frame has fired onLoad event
     */
    isFrameLoaded: false,

    /**
     * Snippet script has loaded
     */
    isSnippetLoaded: false,

    /**
     * Snippet has been loaded inside iframe and reported ready event
     */
    isSnippetReady: false,

    /**
     * Snippet reported ready event but we not marked it as initiated yet
     */
    isSnippetReadyMessageReceived: false,

    /**
     * SSG app is fully loaded (different from isFrameLoaded; frame load - window.onload; ssg load - ssg reported it's loaded)
     */
    isAppLoaded: false,

    /**
     * IFrame send beforeunload event
     */
    isUnloading: false,

    /**
     * User has clicked navigate button on the outline
     */
    isNavigateClicked: false,

    /**
     * IFrame scroll position
     */
    scrollPosition: { x: 0, y: 0 },

    /**
     * Current frame load progress in %
     */
    loadProgress: 0,

    /**
     * Max frame load progress, simulated value from loadProgress to this, in %
     */
    maxLoadProgress: 0,

    /**
     * Date when iframe loading has started
     */
    loadStartDate: null,

    /**
     * Last received editable event from the iframe
     */
    lastEditableEvent: null,

    /**
     * Last received action button clicked event from the iframe
     */
    lastActionButton: null,

    /**
     * Last field which is became highlighted
     */
    lastHighlightedField: null,

    /** Currently content editable field */
    editableField: null,

    /**
     * Container auth token
     */
    authToken: null
});

const studioFrameReducer = (state = getDefaultState(), action: StudioFrameActions | null): State => {
    switch (action?.type) {
        case 'SET_STUDIO_FRAME_URL_PATH': {
            return {
                ...state,
                urlPath: action.data.urlPath,
                urlPathAttrs: {
                    isReplace: action.data.isReplace,
                    isUserTriggered: action.data.isUserTriggered
                },
                urlPathHistory: action.data.urlPathHistory,
                urlPathHistoryIndex: action.data.historyIndex
            };
        }

        case 'SET_STUDIO_FRAME_URL_PATH_HISTORY': {
            return {
                ...state,
                urlPathHistory: action.data.urlPathHistory,
                urlPathHistoryIndex: action.data.historyIndex
            };
        }

        case 'SET_STUDIO_FRAME_URL_PATH_PROGRESS': {
            return {
                ...state,
                urlPathProgress: action.data.urlPath
            };
        }

        case 'SET_STUDIO_FRAME_AVAILABLE': {
            return {
                ...state,
                isAvailable: action.data.isAvailable ?? false
            };
        }

        case 'SET_STUDIO_FRAME': {
            return {
                ...state,
                element: action.data.element ?? null
            };
        }

        case 'SET_STUDIO_FRAME_LOADED': {
            return {
                ...state,
                isFrameLoaded: action.data?.isLoaded ?? false
            };
        }

        case 'SET_STUDIO_APP_LOADED': {
            return {
                ...state,
                isAppLoaded: action.data?.isAppLoaded ?? false
            };
        }

        case 'SET_STUDIO_FRAME_SNIPPET_LOADED': {
            return {
                ...state,
                isSnippetLoaded: action.data?.isSnippetLoaded ?? false
            };
        }

        case 'SET_STUDIO_FRAME_SNIPPET_READY': {
            return {
                ...state,
                isSnippetReady: action.data?.isSnippetReady ?? false,
                isSnippetReadyMessageReceived: action.data?.isMessageReceived ?? false
            };
        }

        case 'SET_STUDIO_FRAME_SCROLL_POSITION': {
            return {
                ...state,
                scrollPosition: {
                    x: action.data.x ?? 0,
                    y: action.data.y ?? 0
                }
            };
        }

        case 'SET_STUDIO_FRAME_PROGRESS': {
            return {
                ...state,
                loadProgress: action.data?.currentProgress,
                maxLoadProgress: action.data?.maxProgress ?? null
            };
        }

        case 'SET_STUDIO_FRAME_LOAD_STARTED_DATE': {
            return {
                ...state,
                loadStartDate: action.data.loadStartDate
            };
        }

        case 'SET_STUDIO_FRAME_EDITABLE_EVENT': {
            return {
                ...state,
                lastEditableEvent: action.data
            };
        }

        case 'SET_STUDIO_FRAME_ACTION_BUTTON': {
            return {
                ...state,
                lastActionButton: action.data,
                isNavigateClicked: action.data?.button === OutlineButtonType.Navigate
            };
        }

        case 'SET_STUDIO_FRAME_HIGHLIGHTED_FIELD': {
            return {
                ...state,
                lastHighlightedField: action.data
            };
        }

        case 'SET_STUDIO_FRAME_UNLOADING': {
            return {
                ...state,
                isUnloading: action.data.isUnloading ?? false,
                isNavigateClicked: action.data.isUnloading ? state.isNavigateClicked : false
            };
        }

        case 'SET_STUDIO_FRAME_EDITABLE_FIELD': {
            return {
                ...state,
                editableField: action.data
            };
        }

        case 'SET_STUDIO_FRAME_AUTH_TOKEN': {
            return {
                ...state,
                authToken: action.data.authToken
            };
        }

        case RESET_STUDIO_ACTION: {
            return getDefaultState();
        }

        default: {
            return state;
        }
    }
};

export default studioFrameReducer;

export const localStorePaths = [];
